//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useEffect, useState } from 'react';
import { useContext } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles } from '@material-ui/core';
import { Grid } from '@material-ui/core';
import { useParams } from 'react-router-dom';
import SimpleBar from 'simplebar-react';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { getCloudFunctions } from '../system/firebase';
import { LayoutContext } from '../common/contexts';
import { setClassNameInDefns } from '../common/definitionHelper';
import useSimpleBarRef from '../common/hooks/useSimpleBarRef';
import { INTERVIEW_CODE_VALID, INTERVIEW_CODE_INVALID, INTERVIEW_NOT_CONFIRMED, INTERVIEW_CODE_EXPIRED } from '../common/constants';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
// import AfitdTypography from '../../common/elemental/AfitdTypography';
import TopNav from '../topNav/TopNav';
import PageMessage from '../common/elemental/PageMessage';
import Footer from '../home/subComponents/Footer';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import InterviewDetails from './InterviewDetails';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: {},
    pageContainer: { backgroundColor: '#FAFAFC', overflow: 'hidden', height: '100%', minHeight: '100%', flexWrap: 'unset' },
    pageHeader: {
        maxHeight: '81px',
        // [theme.breakpoints.down('md')]: { maxHeight: '134px' }, // commented out because no more second row menu -el 20221022
        // [theme.breakpoints.down('sm')]: { maxHeight: '81px' },
    },
    pageContent: {
        flex: 1,
        overflowY: 'hidden',
        minHeight: '100%',
    },
    tempContainer: { alignItems: 'center', marginTop: 50 },
    pageFooter: {},
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        // props,
        // currentPageId,
        // setCurrentPageId,
    } = params;

    const defns = {
        pageContainer: { container: true, direction: 'column' },
        pageHeader: { item: true, lg: 12, md: 12, sm: 12, xs: 12 },
        pageContent: { item: true, container: true },
        tempContainer: { item: true, container: true, direction: 'column' },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
// // A custom hook that builds on useLocation to parse the query string for you.
// const useQuery = () => {
//     const { search } = useLocation();

//     return React.useMemo(() => new URLSearchParams(search), [search]);
// };

const PAGE_ID_PROCESSING_INVITATION_CODE = 1;
const PAGE_ID_INTERVIEW_CODE_INVALID = 2;
const PAGE_ID_INTERVIEW_NOT_CONFIRMED = 3;
const PAGE_ID_INTERVIEW_CODE_EXPIRED = 4;
const PAGE_ID_INTERVIEW_CODE_VALID = 5;
const PAGE_ID_HTTPS_ERROR = 6;

// const PAGE_ID_ACTION_CODE_FAILED_EMAIL_VERIFICATION = 5;

// const PAGE_ID_REDIRECT_TO_HOME_PAGE = 99;

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const JoinInterviewLayout = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    // const appContext = useContext(AppContext);
    // const { authUser } = appContext;
    const context = useContext(LayoutContext);
    // const { setLoadingDialogOpen } = context;
    const { simpleBarRef: pageContentSimpleBarEl, scrollToTop: scrollToPageTop } = useSimpleBarRef();

    const { code, interviewerId, jobSeekerId, employerId } = useParams();

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { isJs, isEmp } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [isShowProgressBar, setIsShowProgressBar] = useState(false);
    const [currentPageId, setCurrentPageId] = useState();
    const [interviewData, setInterviewData] = useState();
    const [token, setToken] = useState();
    const [isRequestOpenLoginDialog, setIsRequestOpenLoginDialog] = useState();
    const [isRequestOpenCreateAccountDialog, setIsRequestOpenCreateAccountDialog] = useState();

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        const processCode = async ({ code } = {}) => {
            setCurrentPageId(PAGE_ID_PROCESSING_INVITATION_CODE);
            try {
                // call different Cloud Function based on the user type (JS, EMP, or additional interviewer)
                const functions = getCloudFunctions();
                let fetchInterviewDetailsByJoinCode = null;
                if (isJs) fetchInterviewDetailsByJoinCode = functions.httpsCallable('fetchInterviewDetailsByJoinCodeJs');
                else if (isEmp) fetchInterviewDetailsByJoinCode = functions.httpsCallable('fetchInterviewDetailsByJoinCodeEmp');
                else fetchInterviewDetailsByJoinCode = functions.httpsCallable('fetchInterviewDetailsByJoinCode');

                const id = isJs ? jobSeekerId : isEmp ? employerId : interviewerId;

                setIsShowProgressBar(true);
                const result = await fetchInterviewDetailsByJoinCode({ code, id });
                setIsShowProgressBar(false);
                const status = _get(result, 'data.status');
                const jwtToken = _get(result, 'data.token.token');
                let interview = _get(result, 'data.interviewData');
                switch (status) {
                    case INTERVIEW_CODE_INVALID: {
                        setCurrentPageId(PAGE_ID_INTERVIEW_CODE_INVALID);
                        break;
                    }
                    case INTERVIEW_NOT_CONFIRMED: {
                        setCurrentPageId(PAGE_ID_INTERVIEW_NOT_CONFIRMED);
                        break;
                    }
                    case INTERVIEW_CODE_EXPIRED: {
                        setCurrentPageId(PAGE_ID_INTERVIEW_CODE_EXPIRED);
                        break;
                    }
                    case INTERVIEW_CODE_VALID: {
                        setCurrentPageId(PAGE_ID_INTERVIEW_CODE_VALID);
                        break;
                    }

                    default:
                    // do nothing
                }
                if (interview) {
                    setInterviewData(interview);
                    setToken(jwtToken);
                }
            } catch (error) {
                console.log('Error fetching Interview data', error?.message);
                setCurrentPageId(PAGE_ID_HTTPS_ERROR);
            }
        };

        if (code) processCode({ code });
    }, [code, interviewerId, jobSeekerId, employerId, isJs, isEmp]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const getPageComponent = () => {
        const pageMessageProps = {
            processing: false,
            interviewJoinCodeInvalid: false,
            interviewNotConfirmed: false,
            interviewJoinCodeExpired: false,
            httpsError: false,
        };
        switch (currentPageId) {
            case PAGE_ID_INTERVIEW_CODE_VALID:
                return <InterviewDetails data={interviewData} token={token} autoLaunch={isJs || isEmp} />;
            case PAGE_ID_INTERVIEW_CODE_INVALID:
                return <PageMessage {...pageMessageProps} interviewJoinCodeInvalid />;
            case PAGE_ID_INTERVIEW_NOT_CONFIRMED:
                return <PageMessage {...pageMessageProps} interviewNotConfirmed />;
            case PAGE_ID_INTERVIEW_CODE_EXPIRED:
                return <PageMessage {...pageMessageProps} interviewJoinCodeExpired />;
            case PAGE_ID_HTTPS_ERROR:
                return <PageMessage {...pageMessageProps} httpsError />;
            case PAGE_ID_PROCESSING_INVITATION_CODE:
                return <PageMessage {...pageMessageProps} processing />;
            default:
                // do nothing
                break;
        }
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props, currentPageId, setCurrentPageId });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <LayoutContext.Provider
            value={{
                ...context,
                scrollToPageTop,
                interviewData,
                token,
                invitationCode: code,
                isRequestOpenLoginDialog,
                setIsRequestOpenLoginDialog,
                isRequestOpenCreateAccountDialog,
                setIsRequestOpenCreateAccountDialog,
            }}
        >
            <Grid {...defns.pageContainer}>
                <Grid {...defns.pageHeader}>
                    <TopNav isShowProgressBar={isShowProgressBar} />
                </Grid>
                <Grid {...defns.pageContent}>
                    <SimpleBar style={{ height: '100%', width: '100%', flex: 1, overflowX: 'hidden' }} ref={pageContentSimpleBarEl}>
                        <div style={{ minHeight: '500px' }}>{getPageComponent()}</div>
                        <Grid {...defns.pageFooter}>
                            <Footer />
                            <br />
                            <br />
                            <br />
                        </Grid>
                    </SimpleBar>
                </Grid>
            </Grid>
        </LayoutContext.Provider>
    );
};

export default JoinInterviewLayout;
